const UTM_STORAGE_KEY = 'belirumah_utm';
const VISITOR_ID_KEY = 'belirumah_visitor_id';
const VISITOR_CREATED_AT_KEY = 'belirumah_visitor_created_at';
const SESSION_ID_KEY = 'belirumah_session_id';
const LAST_ACTIVITY_KEY = 'belirumah_last_activity';
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const VISITOR_TTL = 365 * 24 * 60 * 60 * 1000; // 12 months

const defaultUtm = () => ({
  source: null,
  medium: null,
  campaign: null,
  term: null,
  content: null,
});

function bytesToHex(bytes) {
  return Array.from(bytes)
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

async function createAnalyticsSignatureHeaders(secret, payload) {
  if (typeof window === 'undefined') return {};
  if (!window.crypto || !window.crypto.subtle || typeof TextEncoder === 'undefined') return {};

  const timestamp = Date.now().toString();
  const encoder = new TextEncoder();
  const key = await window.crypto.subtle.importKey(
    'raw',
    encoder.encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const dataToSign = `${timestamp}.${payload}`;
  const signatureBuffer = await window.crypto.subtle.sign('HMAC', key, encoder.encode(dataToSign));

  return {
    'X-Timestamp': timestamp,
    'X-Signature': bytesToHex(new Uint8Array(signatureBuffer)),
  };
}

async function postAnalytics(http, path, payload) {
  if (!process.browser) return {};

  const serializedPayload = JSON.stringify(payload || {});
  const secret = process.env.VUE_APP_ANALYTICS_SIGN_SECRET;
  let signatureHeaders = {};

  if (secret) {
    try {
      signatureHeaders = await createAnalyticsSignatureHeaders(secret, serializedPayload);
    } catch (error) {
      signatureHeaders = {};
    }
  }

  const { data } = await http.post(path, serializedPayload, {
    headers: {
      'Content-Type': 'application/json',
      ...signatureHeaders,
    },
  });

  return data;
}

function getCurrentRouteQuery() {
  if (!process.browser) return {};

  const searchParams = new URLSearchParams(window.location.search || '');
  const query = {};
  for (const [key, value] of searchParams.entries()) {
    query[key] = value;
  }
  return query;
}

export const state = () => ({
  visitorId: null,
  sessionId: null,
  utm: defaultUtm(),
});

export const mutations = {
  SET_VISITOR_ID(state, payload) {
    state.visitorId = payload || null;
  },
  SET_SESSION_ID(state, payload) {
    state.sessionId = payload || null;
  },
  SET_UTM(state, payload) {
    state.utm = {
      ...state.utm,
      ...payload,
    };
  },
  RESET(state) {
    state.visitorId = null;
    state.sessionId = null;
    state.utm = defaultUtm();
  },
};

export const actions = {
  ensureContext({ state, commit }, routeQuery = {}) {
    if (!process.browser) return;

    const query = routeQuery || {};
    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
    const hasNewUtm = utmKeys.some((key) => Boolean(query[key]));

    if (hasNewUtm) {
      const nextUtm = {};
      if (query.utm_source) nextUtm.source = query.utm_source;
      if (query.utm_medium) nextUtm.medium = query.utm_medium;
      if (query.utm_campaign) nextUtm.campaign = query.utm_campaign;
      if (query.utm_term) nextUtm.term = query.utm_term;
      if (query.utm_content) nextUtm.content = query.utm_content;
      commit('SET_UTM', nextUtm);

      const persistPayload = {};
      if (query.utm_source) persistPayload.utm_source = query.utm_source;
      if (query.utm_medium) persistPayload.utm_medium = query.utm_medium;
      if (query.utm_campaign) persistPayload.utm_campaign = query.utm_campaign;
      if (query.utm_term) persistPayload.utm_term = query.utm_term;
      if (query.utm_content) persistPayload.utm_content = query.utm_content;
      window.sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(persistPayload));
    } else if (!state.utm.source) {
      const storedUtmRaw = window.sessionStorage.getItem(UTM_STORAGE_KEY);
      if (storedUtmRaw) {
        try {
          const storedUtm = JSON.parse(storedUtmRaw);
          commit('SET_UTM', {
            source: storedUtm.utm_source || null,
            medium: storedUtm.utm_medium || null,
            campaign: storedUtm.utm_campaign || null,
            term: storedUtm.utm_term || null,
            content: storedUtm.utm_content || null,
          });
        } catch (error) {
          window.sessionStorage.removeItem(UTM_STORAGE_KEY);
        }
      }
    }

    const now = Date.now();
    const generateUUID = () => {
      if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
      return `${now.toString(36)}${Math.random().toString(36).slice(2)}`;
    };

    let visitorId = window.localStorage.getItem(VISITOR_ID_KEY);
    const visitorCreatedAtRaw = window.localStorage.getItem(VISITOR_CREATED_AT_KEY);
    const visitorCreatedAt = visitorCreatedAtRaw ? parseInt(visitorCreatedAtRaw, 10) : NaN;
    const isVisitorExpired =
      Number.isFinite(visitorCreatedAt) &&
      visitorCreatedAt > 0 &&
      now - visitorCreatedAt > VISITOR_TTL;

    if (!visitorId || isVisitorExpired) {
      visitorId = `v_${generateUUID()}`;
      window.localStorage.setItem(VISITOR_ID_KEY, visitorId);
      window.localStorage.setItem(VISITOR_CREATED_AT_KEY, now.toString());
    } else if (!Number.isFinite(visitorCreatedAt) || visitorCreatedAt <= 0) {
      window.localStorage.setItem(VISITOR_CREATED_AT_KEY, now.toString());
    }

    let sessionId = window.localStorage.getItem(SESSION_ID_KEY);
    const lastActivityRaw = window.localStorage.getItem(LAST_ACTIVITY_KEY);
    let isExpired = false;

    if (lastActivityRaw) {
      const diff = now - parseInt(lastActivityRaw, 10);
      if (diff > SESSION_TIMEOUT) isExpired = true;
    }

    if (!sessionId || isExpired) {
      sessionId = `s_${generateUUID()}`;
      window.localStorage.setItem(SESSION_ID_KEY, sessionId);
    }

    window.localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
    commit('SET_VISITOR_ID', visitorId);
    commit('SET_SESSION_ID', sessionId);
  },

  async recordImpressionBanner({ dispatch, state }, payload) {
    if (!payload) return {};
    dispatch('ensureContext', getCurrentRouteQuery());

    return await postAnalytics(this.$http, '/api/analytics/record_banner_impression', {
      banner_uuid: payload,
      utm_source: state.utm.source,
      utm_medium: state.utm.medium,
      visitor_id: state.visitorId,
      session_id: state.sessionId,
    });
  },

  async recordVisitBanner({ dispatch, state }, payload) {
    if (!payload) return {};
    dispatch('ensureContext', getCurrentRouteQuery());

    return await postAnalytics(this.$http, '/api/analytics/record_banner_visit', {
      banner_uuid: payload,
      utm_source: state.utm.source,
      utm_medium: state.utm.medium,
      visitor_id: state.visitorId,
      session_id: state.sessionId,
    });
  },
};
